import CourseCard, { Course } from "@/components/design-system/course-card";
import AssignmentCard from "@/components/design-system/assignment-card";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import {
  selectStudentDashboardLoading,
  selectStudentDashboardError,
  fetchStudentDashboardData,
} from "@/store/slices/studentDashboardSlice";
import {
  selectStudentAssignments,
  selectStudentAssignmentsLoading,
  selectStudentAssignmentsError,
  fetchStudentAssignments,
} from "@/store/slices/studentAssignmentsSlice";
import { useNavigate } from "react-router";
import { Loader2 } from "lucide-react";

/** Same preview limit as StudentClassDashboard (`CLASS_LIST_PREVIEW_COUNT`). */
const COURSE_LIST_PREVIEW_COUNT = 2;

export default function StudentDashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [showAllCourses, setShowAllCourses] = useState(false);

  const dashboardData = useSelector(
    (state: RootState) => state.studentDashboard.data
  );
  const loading = useSelector(selectStudentDashboardLoading);
  const error = useSelector(selectStudentDashboardError);

  // Assignment data from Redux
  const assignmentData = useSelector(selectStudentAssignments);
  const assignmentsLoading = useSelector(selectStudentAssignmentsLoading);
  const assignmentsError = useSelector(selectStudentAssignmentsError);

  // Fetch student dashboard data on component mount
  useEffect(() => {
    dispatch(fetchStudentDashboardData());
    dispatch(fetchStudentAssignments({}));
  }, [dispatch]);

  const allCourses = dashboardData?.grades ?? [];
  const courseCount = allCourses.length;
  const hasMoreCourses = courseCount > COURSE_LIST_PREVIEW_COUNT;
  const visibleCourses = showAllCourses
    ? allCourses
    : allCourses.slice(0, COURSE_LIST_PREVIEW_COUNT);

  // Define course status type
  type CourseStatus = "NEW" | "COMPLETED" | "REVIEW" | "PENDING";

  // Define assignment status type for AssignmentCard component
  type AssignmentStatus = "In Progress" | "Completed" | "New Feedback";

  // Helper function to convert assignment API status to component status
  const getAssignmentStatus = (status: string): AssignmentStatus => {
    // Map API status to AssignmentCard status values
    return status as AssignmentStatus;
  };

  // Helper function to map API status to Course component status
  const mapStatusToCourseStatus = (status: string): CourseStatus => {
    // Map the API status values to the expected Course status values
    return status as CourseStatus;
  };

  // Render content based on loading and error states
  const renderCourseContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="animate-spin" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-red-500 py-4">
          Error loading course data: {error}
        </div>
      );
    }

    if (!dashboardData?.grades || dashboardData.grades.length === 0) {
      return <div className="text-gray-500 py-4">No course data available</div>;
    }

    return (
      <div className="space-y-4">
        {visibleCourses.map((grade, index) => {
          const course: Course = {
            title: grade.grade || "",
            status: mapStatusToCourseStatus(grade.status || ""),
            imageUrl: "/images/course_thumbnail.png", // Default image path
            lessonsCompleted: grade.dash?.lessons || 0,
            totalLessons: parseInt(grade.lessons || "0"),
            unitsCompleted: grade.dash?.units || 0,
            totalUnits: parseInt(grade.units || "0"),
            submissionsCount: parseInt(grade.submissions || "0"),
            feedbacksCount: parseInt(grade.feedbacks || "0"),
            completionPercent: parseInt(
              (grade.dash?.completion || "0%").replace("%", "")
            ),
            onViewCourse: () => {
              navigate(`/course-management/view-course/${grade.id}`);
            },
          };

          return (
            <div key={grade.id || index} className="mb-4">
              <CourseCard course={course} />
            </div>
          );
        })}
      </div>
    );
  };

  // Render unit assignments
  const renderUnitAssignments = () => {
    if (assignmentsLoading) {
      return (
        <div className="flex justify-center items-center w-full py-4">
          <Loader2 className="animate-spin" />
        </div>
      );
    }

    if (assignmentsError) {
      return (
        <div
          className={`flex flex-col mb-2 justify-center items-center h-[150px] w-full bg-sky-50 rounded-[12px]`}
        >
          <p className="text-red-500 text-[16px] font-bold mb-[9px]">
            Error loading unit assignments: {assignmentsError}
          </p>
        </div>
      );
    }

    const unitAssignments =
      assignmentData?.assignments?.filter(
        (assignment) => assignment.assign_type === "UNIT"
      ) || [];

    if (unitAssignments.length === 0) {
      return (
        <div
          className={`flex flex-col mb-2 justify-center items-center h-[150px] w-full bg-sky-50 rounded-[12px]`}
        >
          <p className="text-[#1F1F1F] text-[18px] font-bold mb-[9px]">
            No Unit Assignments assigned yet.
          </p>

          <div>
            <p className="text-[#1F1F1F] text-[18px] font-medium mb-[28px]">
              Your teacher-assigned tasks will appear here.
            </p>
          </div>
        </div>
      );
    }

    return unitAssignments.map((assignment) => (
      <AssignmentCard
        key={assignment.id}
        courseTitle={assignment.name}
        status={getAssignmentStatus(assignment.status.label)}
        unitName={assignment.unit?.name}
        lessonName={assignment.lesson?.name}
        dateTime={assignment.created_date}
        onViewAssignment={() => {
          navigate(`/course-management/view-course/${assignment.grade.id}`, {
            state: {
              selectedTab: "assignment",
              selectedCardId: assignment.id,
              assignmentType:
                (assignment as { assign_type: string })?.assign_type === "UNIT"
                  ? "unit"
                  : "general",
              lessonId:
                (assignment as { lesson: { id: string } })?.lesson?.id ||
                assignment.lesson?.id ||
                null,
              unitId:
                (assignment as { unit: { id: string } })?.unit?.id ||
                assignment.unit?.id ||
                null,
            },
          });
        }}
      />
    ));
  };

  // Render general assignments
  const renderGeneralAssignments = () => {
    if (assignmentsLoading) {
      return (
        <div className="flex justify-center items-center w-full py-4">
          <Loader2 className="animate-spin" />
        </div>
      );
    }

    if (assignmentsError) {
      return (
        <div
          className={`flex flex-col mb-2 justify-center items-center h-[150px] w-full bg-sky-50 rounded-[12px]`}
        >
          <p className="text-red-500 text-[16px] font-bold mb-[9px]">
            Error loading general assignments: {assignmentsError}
          </p>
        </div>
      );
    }

    const generalAssignments =
      assignmentData?.assignments?.filter(
        (assignment) => assignment.assign_type === "GENERAL"
      ) || [];

    if (generalAssignments.length === 0) {
      return (
        <div
          className={`flex flex-col mb-2 justify-center items-center h-[150px] w-full bg-sky-50 rounded-[12px]`}
        >
          <p className="text-[#1F1F1F] text-[18px] font-bold mb-[9px]">
            No General Assignments assigned yet.
          </p>

          <div>
            <p className="text-[#1F1F1F] text-[18px] font-medium mb-[28px]">
              Your teacher-assigned tasks will appear here.
            </p>
          </div>
        </div>
      );
    }

    return generalAssignments.map((assignment) => (
      <AssignmentCard
        key={assignment.id}
        courseTitle={assignment.name}
        status={getAssignmentStatus(assignment.status.label)}
        dateTime={assignment.created_date}
        onViewAssignment={() => {
          navigate(`/course-management/view-course/${assignment.grade.id}`, {
            state: {
              selectedTab: "assignment",
              selectedCardId: assignment.id,
              assignmentType:
                (assignment as { assign_type: string })?.assign_type === "UNIT"
                  ? "unit"
                  : "general",
              lessonId:
                (assignment as { lesson: { id: string } })?.lesson?.id ||
                assignment.lesson?.id ||
                null,
              unitId:
                (assignment as { unit: { id: string } })?.unit?.id ||
                assignment.unit?.id ||
                null,
            },
          });
        }}
      />
    ));
  };
  const generalAssignments =
    assignmentData?.assignments?.filter(
      (assignment) => assignment.assign_type === "GENERAL"
    ) || [];

  const unitAssignments =
    assignmentData?.assignments?.filter(
      (assignment) => assignment.assign_type === "UNIT"
    ) || [];

  const handleViewAll = () => {
    navigate(`/course-management/view-course/${dashboardData?.grades[0].id}`, {
      state: {
        selectedTab: "assignment",
      },
    });
  };

  return (
    <div className="bg-white rounded-[12px] p-[24px] w-full overflow-hidden h-full flex flex-1 flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-[20px] text-[#1F1F1F] font-bold">
          {courseCount > 1
            ? `Courses(${String(courseCount).padStart(2, "0")})`
            : "Course"}
        </h2>
        {hasMoreCourses && !showAllCourses ? (
          <button
            type="button"
            onClick={() => setShowAllCourses(true)}
            className="text-[#5C88C5] text-[16px] font-[600] cursor-pointer outline-none border-none"
          >
            View all
          </button>
        ) : null}
      </div>
      {renderCourseContent()}
      <div className="mb-[24px] mt-[24px]">
        <div className="flex justify-between items-center mb-[19px]">
          <h2 className="text-[20px] text-[#1F1F1F] font-bold">
            Unit Assignments ({String(unitAssignments.length).padStart(2, "0")})
          </h2>
          {unitAssignments.length > 0 && (
            <button
              onClick={handleViewAll}
              className="text-[#5C88C5] text-[16px] font-[600] cursor-pointer outline-none border-none"
            >
              View all
            </button>
          )}
        </div>
        <div className="flex justify-start gap-[17px] flex-wrap">
          {renderUnitAssignments()}
        </div>
      </div>
      <div className="mb-[24px]">
        <h2 className="text-[20px] text-[#1F1F1F] font-bold mb-[19px]">
          General Assignments (
          {String(generalAssignments.length).padStart(2, "0")})
        </h2>
        <div className="flex justify-start gap-[16px] flex-wrap">
          {renderGeneralAssignments()}
        </div>
      </div>
    </div>
  );
}
